import { z } from 'zod';

export const BreakEvenQuerySchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
  })
  .refine((value) => Date.parse(value.from) <= Date.parse(value.to), {
    message: 'from must be less than or equal to to',
    path: ['from'],
  });
export type BreakEvenQuery = z.infer<typeof BreakEvenQuerySchema>;

const NonNegativeCentsStringSchema = z.string().regex(/^\d+$/);
const IntegerCentsStringSchema = z.string().regex(/^-?\d+$/);

export const BreakEvenReportSchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    saleCents: NonNegativeCentsStringSchema,
    directCostCents: NonNegativeCentsStringSchema,
    commissionCents: NonNegativeCentsStringSchema,
    fixedCostCents: NonNegativeCentsStringSchema,
    recurringFixedCostCents: NonNegativeCentsStringSchema,
    installmentFixedCostCents: NonNegativeCentsStringSchema,
    contributionCents: IntegerCentsStringSchema,
    contributionMarginBps: z.number().int().nullable(),
    breakEvenCents: NonNegativeCentsStringSchema.nullable(),
    uncovered: z.boolean(),
    empty: z.boolean(),
  })
  .refine((value) => !value.uncovered || value.breakEvenCents === null, {
    message: 'breakEvenCents must be null when uncovered is true',
    path: ['breakEvenCents'],
  })
  .refine((value) => value.uncovered === BigInt(value.contributionCents) <= 0n, {
    message: 'uncovered must be true iff contributionCents <= 0',
    path: ['uncovered'],
  })
  .refine(
    (value) =>
      value.empty ===
      (value.saleCents === '0' &&
        value.directCostCents === '0' &&
        value.commissionCents === '0' &&
        value.fixedCostCents === '0'),
    {
      message: 'empty must be true iff sale, cost, commission and fixed cost are all zero',
      path: ['empty'],
    },
  )
  .refine(
    (value) =>
      BigInt(value.recurringFixedCostCents) + BigInt(value.installmentFixedCostCents) ===
      BigInt(value.fixedCostCents),
    {
      message: 'fixedCostCents must equal recurringFixedCostCents + installmentFixedCostCents',
      path: ['fixedCostCents'],
    },
  );
export type BreakEvenReport = z.infer<typeof BreakEvenReportSchema>;
